/**
 * «Задачи этапа» в «Моих задачах» партнёра: задачи карточки открытия (v2, по шаблону плана) идут первыми,
 * прежние пункты этапа (статьи, курсы, авто-пункты) — после них. Чистая логика без React: счётчики, срок,
 * подсказка «не хватает», оптимистичное обновление кеша react-query — покрыта stage-tasks.test.ts.
 */
import type {
  MyCardChecklistItemDto,
  MyCardTaskDto,
  MyCardTaskSetStatus,
  MyOpeningTasksDto,
  MyPlanTaskDto,
  MyRoomDto,
  UkResultDto,
} from '../../lib/api/openingTasks';
import { plural } from '../ds/tokens';
import { isRoomRejected } from './utils';

/**
 * Задача карточки в любом из двух видов: из «Моих задач» (`MyCardTaskDto`, сторона «партнёр» / «обе») или из плана
 * открытия в визарде (`MyPlanTaskDto`, ещё и «УК»). Строка `CardTaskRow` рисует обе одинаково.
 */
export type AnyCardTask = MyCardTaskDto | MyPlanTaskDto;

/** Сегодня по Москве, `YYYY-MM-DD` — день, по которому сервер считает просрочку (у партнёров своя timezone у ТТ, у карточки — московская). */
export function todayMsk(now: Date = new Date()): string {
  // en-CA даёт ГГГГ-ММ-ДД.
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/** «12.10» (тот же год, что и сегодня) или «12.01.2027»; кривая строка — пусто. */
export function fmtShortDate(ymd: string | null | undefined, today: string = todayMsk()): string {
  const m = ymd ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd) : null;
  if (!m) return '';
  return m[1] === today.slice(0, 4) ? `${m[3]}.${m[2]}` : `${m[3]}.${m[2]}.${m[1]}`;
}

/** Срок задачи для строки: «просрочено с 12.10» (красным), «срок 20.10»; выполненной и без срока — нет. */
export function dueLabel(task: Pick<MyCardTaskDto, 'dueDate' | 'overdue' | 'done'>, today: string = todayMsk()): { text: string; overdue: boolean } | null {
  if (task.done || !task.dueDate) return null;
  const d = fmtShortDate(task.dueDate, today);
  if (!d) return null;
  return task.overdue ? { text: `просрочено с ${d}`, overdue: true } : { text: `срок ${d}`, overdue: false };
}

/** Прогресс блока: задачи карточки + прежние пункты этапа вместе («Задачи этапа N / M»). */
export function stageProgress(data: Pick<MyOpeningTasksDto, 'cardTasks' | 'stageTasks'>): { done: number; total: number } {
  const cards = data.cardTasks ?? [];
  return {
    done: cards.filter((t) => t.done).length + data.stageTasks.filter((t) => t.done).length,
    total: cards.length + data.stageTasks.length,
  };
}

/** «A, B, C и ещё 4»: у карточки с 19 задачами полный список в плашке не нужен. */
export function missingHint(titles: readonly string[], max = 3): string {
  if (titles.length <= max) return titles.join(', ');
  const rest = titles.length - max;
  return `${titles.slice(0, max).join(', ')} и ещё ${rest}`;
}

/**
 * «В работе» и «выполненные» (владелец 31.10.2026: в «Моих задачах» — только то, что сейчас в работе, выполненное
 * прячется за кнопку). Порядок внутри каждой части — как пришёл с сервера.
 */
export function splitStageTasks<T extends { done: boolean }>(items: readonly T[]): { open: T[]; done: T[] } {
  const open: T[] = [];
  const done: T[] = [];
  for (const it of items) (it.done ? done : open).push(it);
  return { open, done };
}

/**
 * Плашка «По помещениям: 2 новых сообщения, 1 момент к исправлению» над задачами этапа. Считаются помещения, которые
 * видны в списке (отказанные «Не подходит» в нём не показываются — их не открыть). Нет ни того, ни другого — null.
 */
export function roomsNotice(
  rooms: ReadonlyArray<Pick<MyRoomDto, 'unread' | 'openMoments' | 'stageCode' | 'status'>>,
): { text: string; unread: number; moments: number } | null {
  let unread = 0;
  let moments = 0;
  for (const r of rooms) {
    if (isRoomRejected(r.stageCode, r.status)) continue;
    unread += r.unread;
    moments += r.openMoments;
  }
  const parts: string[] = [];
  if (unread > 0) parts.push(`${unread} ${plural(unread, 'новое сообщение', 'новых сообщения', 'новых сообщений')}`);
  if (moments > 0) parts.push(`${moments} ${plural(moments, 'момент', 'момента', 'моментов')} к исправлению`);
  if (parts.length === 0) return null;
  return { text: `По помещениям: ${parts.join(', ')}`, unread, moments };
}

/** Метка стороны «УК» на строке задачи: «УК · Отдел стройки» или «Выполняет УК»; задача партнёра — null. */
export function ukSideLabel(task: { side: string; deptLabel?: string | null }): string | null {
  if (task.side !== 'uk') return null;
  const dept = task.deptLabel?.trim();
  return dept ? `УК · ${dept}` : 'Выполняет УК';
}

/** Выполнено пунктов чек-листа / всего. */
export function checklistProgress(task: Pick<MyCardTaskDto, 'checklist'>): { done: number; total: number } {
  return { done: task.checklist.filter((i) => i.done).length, total: task.checklist.length };
}

/** Что ставит клик по «Сделано»: выполненную — снимает (todo), остальные — выполняет (в том числе вернувшуюся на доработку). */
export function toggleTarget(task: Pick<MyCardTaskDto, 'done'>): MyCardTaskSetStatus {
  return task.done ? 'todo' : 'done';
}

/**
 * Вторая кнопка рядом с «Сделано» (FB-32): задача не начата («К выполнению» / «На доработке») — «Взять в работу»
 * (→ in_progress); уже в работе — «Вернуть в план» (→ todo); выполненной — нет (её снимает сама «Сделано»).
 */
export function workAction(task: Pick<MyCardTaskDto, 'done' | 'status'>): { label: string; target: MyCardTaskSetStatus } | null {
  if (task.done) return null;
  if (task.status === 'in_progress') return { label: 'Вернуть в план', target: 'todo' };
  return { label: 'Взять в работу', target: 'in_progress' };
}

/** Подпись статуса, когда он не «обычный»: «На доработке» должна быть заметна, «В работе» — подсказка. */
export function statusBadge(status: MyCardTaskDto['status']): { text: string; tone: 'red' | 'blue' } | null {
  if (status === 'rework') return { text: 'На доработке', tone: 'red' };
  if (status === 'in_progress') return { text: 'В работе', tone: 'blue' };
  return null;
}

/** Кому показывать «Проверит …»: приёмка нужна; роль не указана — просто «Нужна приёмка». */
export function reviewerLabel(task: Pick<MyCardTaskDto, 'requiresApproval' | 'reviewerRole'>): string | null {
  if (!task.requiresApproval) return null;
  const r = task.reviewerRole?.trim();
  return r ? `Проверит ${r}` : 'Нужна приёмка';
}

/** Хвост: «с этапа «Помещение»»; название этапа неизвестно — «с прошлого этапа». */
export function carriedLabel(task: Pick<MyCardTaskDto, 'carriedFromStage' | 'carriedFromLabel'>): string | null {
  if (!task.carriedFromStage) return null;
  return task.carriedFromLabel ? `с этапа «${task.carriedFromLabel}»` : 'с прошлого этапа';
}

/** Поля задачи, которые меняет отметка «Сделано». */
type StatusPatchable = Pick<MyCardTaskDto, 'status' | 'done' | 'doneAt' | 'dueDate' | 'overdue'>;

/**
 * Задача с новым статусом (оптимистично; общая для «Моих задач» и плана). Выполненная не просрочена; снятая —
 * по сроку и сегодняшнему дню (точнее скажет повторная загрузка).
 */
export function patchTaskStatus<T extends StatusPatchable>(t: T, status: MyCardTaskSetStatus, now: Date = new Date()): T {
  const done = status === 'done';
  return {
    ...t,
    status,
    done,
    doneAt: done ? now.toISOString() : null,
    overdue: !done && !!t.dueDate && t.dueDate < todayMsk(now),
  };
}

/**
 * Задача с новой отметкой пункта чек-листа (оптимистично). Чужой индекс — без изменений.
 * Пункт-«ссылка»: при отметке получает присланную `url`, при снятии — теряет её (как на сервере).
 */
export function patchChecklistItem<T extends { checklist: MyCardChecklistItemDto[] }>(
  t: T,
  index: number,
  done: boolean,
  url?: string | null,
): T {
  return {
    ...t,
    checklist: t.checklist.map((it, i) => {
      if (i !== index) return it;
      return it.kind === 'link' ? { ...it, done, url: done ? (url ?? it.url ?? null) : null } : { ...it, done };
    }),
  };
}

/** Копия ответа «Мои задачи» с новым статусом задачи карточки (оптимистичное обновление). Чужой id — как есть. */
export function withCardTaskStatus(data: MyOpeningTasksDto, taskId: string, status: MyCardTaskSetStatus, now: Date = new Date()): MyOpeningTasksDto {
  return {
    ...data,
    cardTasks: (data.cardTasks ?? []).map((t) => (t.id === taskId ? patchTaskStatus(t, status, now) : t)),
  };
}

/**
 * Короткий текст дат партнёра для строки и подсказки: «начали 01.10 · закончим 15.10»; одной даты нет — только вторая;
 * обеих нет — null. Только для информации (FB-32): к плану отношения не имеет.
 */
export function partnerDatesText(t: Pick<MyCardTaskDto, 'partnerStartDate' | 'partnerDueDate'>, today: string = todayMsk()): string | null {
  const parts = [
    t.partnerStartDate ? `начали ${fmtShortDate(t.partnerStartDate, today)}` : '',
    t.partnerDueDate ? `закончим ${fmtShortDate(t.partnerDueDate, today)}` : '',
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/** Ошибка пары дат до отправки: окончание раньше начала (одной даты нет — проверять нечего). */
export function partnerDatesError(start: string | null, due: string | null): string | null {
  return start && due && due < start ? 'Окончание не может быть раньше начала' : null;
}

/** Задача с новыми датами партнёра (оптимистично): ключ не передан — поле не трогаем. */
export function patchPartnerDates<T extends Pick<MyCardTaskDto, 'partnerStartDate' | 'partnerDueDate'>>(
  t: T,
  dates: { partnerStartDate?: string | null; partnerDueDate?: string | null },
): T {
  return {
    ...t,
    partnerStartDate: dates.partnerStartDate !== undefined ? dates.partnerStartDate : t.partnerStartDate,
    partnerDueDate: dates.partnerDueDate !== undefined ? dates.partnerDueDate : t.partnerDueDate,
  };
}

/** Копия ответа «Моих задач» с новыми датами партнёра у задачи. Чужой id — как есть. */
export function withPartnerDates(data: MyOpeningTasksDto, taskId: string, dates: { partnerStartDate?: string | null; partnerDueDate?: string | null }): MyOpeningTasksDto {
  return { ...data, cardTasks: (data.cardTasks ?? []).map((t) => (t.id === taskId ? patchPartnerDates(t, dates) : t)) };
}

/** Копия ответа с новой отметкой пункта чек-листа (оптимистично). Чужой id или индекс — как есть. */
export function withChecklistItem(data: MyOpeningTasksDto, taskId: string, index: number, done: boolean, url?: string | null): MyOpeningTasksDto {
  return {
    ...data,
    cardTasks: (data.cardTasks ?? []).map((t) => (t.id === taskId ? patchChecklistItem(t, index, done, url) : t)),
  };
}

// ───────────────────────── Пункт-«ссылка» ─────────────────────────

export const LINK_URL_MAX = 500;

/**
 * Ссылка, как её вставили: пробелы по краям срезаются; адрес без схемы («vk.com/club1») получает https://.
 * Строка со схемой (в том числе чужой) не трогается — её отвергнет проверка.
 */
export function normalizeLinkInput(raw: string): string {
  const s = raw.trim();
  if (!s || /^[a-z][a-z0-9+.-]*:/i.test(s)) return s;
  return /^[^\s/]+\.[^\s/]+/.test(s) ? `https://${s}` : s;
}

/**
 * Проверка ссылки до отправки: http(s) и не длиннее 500 символов. Хост группы ВК проверяет сервер (ответ 400 с понятным
 * текстом показывается тут же) — вики не знает, куда пункт сохраняется.
 */
export function checkLinkUrl(raw: string): { url: string } | { error: string } {
  const url = normalizeLinkInput(raw);
  if (!url) return { error: 'Вставьте ссылку' };
  if (url.length > LINK_URL_MAX) return { error: `Ссылка длиннее ${LINK_URL_MAX} символов` };
  try {
    const u = new URL(url);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') throw new Error('scheme');
  } catch {
    return { error: 'Нужна ссылка, начинающаяся с http:// или https://' };
  }
  return { url };
}

/** Адрес для `href`: только http(s), иначе null (`javascript:` и прочее не открываем). */
export function safeHttpUrl(url: string | null | undefined): string | null {
  const s = url?.trim();
  if (!s) return null;
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null;
  } catch {
    return null;
  }
}

/** Короткая подпись ссылки: без схемы, www и хвостового «/» (`vk.com/club1`); длинное — с многоточием. */
export function shortLinkLabel(url: string, max = 42): string {
  const s = url.trim().replace(/^https?:\/\/(www\.)?/i, '').replace(/\/+$/, '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// ───────────────────────── «От управляющей компании» ─────────────────────────

/** Строка блока «От управляющей компании»: задача УК, присланные ссылки и соседние отметки («Доступ выдан ✓ / ждём»). */
export interface UkResultView {
  id: string;
  title: string;
  status: UkResultDto['status'];
  /** Присланные ссылки: подпись пункта, адрес для `href`, короткая подпись адреса. */
  links: Array<{ title: string; href: string; label: string }>;
  /** Остальные пункты чек-листа: «Доступ выдан ✓» или «Доступ выдан — ждём». */
  checks: Array<{ title: string; done: boolean; text: string }>;
}

/**
 * Что показать партнёру из `ukResults`: у каждой задачи — отмеченные пункты-«ссылки» с рабочим адресом (кликабельно) и
 * прочие пункты галочкой. Неотмеченная ссылка в списке «проверок» («ссылку ждём»); пустой результат (нет ни одной
 * ссылки) не показывается вовсе.
 */
export function ukResultViews(results: readonly UkResultDto[] | null | undefined): UkResultView[] {
  const out: UkResultView[] = [];
  for (const r of results ?? []) {
    const links: UkResultView['links'] = [];
    const checks: UkResultView['checks'] = [];
    for (const it of r.items) {
      const href = it.kind === 'link' && it.done ? safeHttpUrl(it.url) : null;
      if (href) {
        links.push({ title: it.title, href, label: shortLinkLabel(href) });
        continue;
      }
      checks.push({ title: it.title, done: it.done, text: it.done ? `${it.title} ✓` : `${it.title} — ждём` });
    }
    if (links.length > 0) out.push({ id: r.id, title: r.taskTitle, status: r.status, links, checks });
  }
  return out;
}
